import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Send, CalendarClock, Trash2, Check, X, CheckSquare, FileEdit, Paperclip } from "lucide-react";

function Markdownish({ text }) {
  return (
    <div className="text-sm leading-relaxed whitespace-pre-wrap">
      {text.split("\n").map((line, i) => {
        const bold = line.replace(/\*\*(.+?)\*\*/g, "$1");
        if (/^#{1,3}\s/.test(line)) return <div key={i} className="font-bold mt-2">{line.replace(/^#+\s/, "")}</div>;
        if (/^[-*]\s/.test(line)) return <div key={i} className="ml-3">• {bold.replace(/^[-*]\s/, "")}</div>;
        return <div key={i}>{bold}</div>;
      })}
    </div>
  );
}

export default function Copilot() {
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [image, setImage] = useState(null); // data: URL of an attached image
  const [sending, setSending] = useState(false);
  const [actions, setActions] = useState([]);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const loadMessages = () => api.get("/copilot/messages").then((r) => setMessages(r.data));
  const loadActions = () => api.get("/agent/actions").then((r) => setActions(r.data));
  useEffect(() => { loadMessages(); loadActions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const img = text ? null : image; // quick-action buttons never carry an image
    const raw = text || input;
    const msg = raw.trim() || (img ? "What's in this image?" : "");
    if ((!msg && !img) || sending) return;
    setInput("");
    setImage(null);
    setMessages((m) => [...m, { id: "tmp", role: "user", content: msg, image: img }]);
    setSending(true);
    try {
      await api.post("/copilot/chat", { message: msg, image: img });
      await loadMessages();
    } catch (e) {
      toast.error("AI error: " + (e.response?.data?.detail || e.message));
      loadMessages();
    } finally {
      setSending(false);
    }
  };

  const planDay = async () => {
    setSending(true);
    setMessages((m) => [...m, { id: "tmp", role: "user", content: "Plan my day." }]);
    try {
      const { data } = await api.post("/copilot/plan-day");
      setMessages((m) => [...m.filter((x) => x.id !== "tmp"), { id: "u", role: "user", content: "Plan my day." }, { id: "p", role: "assistant", content: data.plan }]);
    } finally {
      setSending(false);
    }
  };

  const clear = async () => { await api.delete("/copilot/messages"); setMessages([]); };
  const approve = async (id) => { await api.post(`/agent/actions/${id}/approve`); toast.success("Approved & executed"); loadActions(); };
  const reject = async (id) => { await api.post(`/agent/actions/${id}/reject`); loadActions(); };

  const pending = actions.filter((a) => a.status === "draft");

  return (
    <div className="space-y-6" data-testid="copilot-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mono text-ai flex items-center gap-1.5"><Sparkles size={13} /> AI Copilot</div>
          <h1 className="text-4xl font-black tracking-tighter">Ask anything.</h1>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="chat" className="rounded-sm" data-testid="tab-chat">Chat</TabsTrigger>
          <TabsTrigger value="actions" className="rounded-sm" data-testid="tab-actions">
            Approvals {pending.length > 0 && <span className="ml-1.5 bg-ai text-white rounded-sm px-1.5 text-xs">{pending.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat">
          <div className="flex gap-2 mb-4 flex-wrap">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={planDay} disabled={sending} data-testid="plan-day-button">
              <CalendarClock size={14} className="mr-1.5" /> Plan my day
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => send("Summarize my open tasks and what I should focus on.")} disabled={sending}>
              Summarize tasks
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => send("What are my biggest risks and blockers right now?")} disabled={sending}>
              Find risks
            </Button>
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" className="rounded-sm text-slate-500 ml-auto" onClick={clear} data-testid="clear-chat-button">
                <Trash2 size={14} className="mr-1.5" /> Clear
              </Button>
            )}
          </div>

          <Card className="rounded-sm border-border shadow-none p-0 flex flex-col h-[520px]">
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
              {messages.length === 0 && !sending && (
                <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                  <div className="w-12 h-12 bg-ai/10 text-ai rounded-sm flex items-center justify-center mb-3"><Sparkles size={22} /></div>
                  <p className="text-sm max-w-xs">Ask about your tasks, notes and priorities. The copilot has full workspace context.</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-sm px-4 py-2.5 ${m.role === "user" ? "bg-slate-950 text-white" : "bg-muted text-slate-900 border border-border"}`}>
                    {m.image && <img src={m.image} alt="attachment" className="rounded-sm mb-2 max-h-48 w-auto" />}
                    {m.role === "assistant" ? <Markdownish text={m.content} /> : <span className="text-sm">{m.content}</span>}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-muted border border-border rounded-sm px-4 py-2.5 ai-pulse">
                    <span className="text-sm text-slate-500">Thinking…</span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className="border-t border-border p-3">
              {image && (
                <div className="relative inline-block mb-2">
                  <img src={image} alt="to send" className="h-16 w-auto rounded-sm border border-border" />
                  <button onClick={() => setImage(null)} data-testid="copilot-image-remove"
                    className="absolute -top-2 -right-2 bg-slate-950 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    <X size={12} />
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="copilot-image-input" />
                <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={sending}
                  className="rounded-sm shrink-0" title="Attach an image" data-testid="copilot-attach-button">
                  <Paperclip size={16} />
                </Button>
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={image ? "Ask about this image…" : "Ask your copilot…"} className="rounded-sm" data-testid="copilot-input" />
                <Button onClick={() => send()} disabled={sending} className="rounded-sm bg-ai hover:bg-blue-800" data-testid="copilot-send-button">
                  <Send size={16} />
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="actions">
          <Card className="rounded-sm border-border shadow-none p-5">
            <h3 className="text-xl font-bold tracking-tight mb-1">Agent Actions</h3>
            <p className="text-sm text-slate-600 mb-4">Review and approve actions proposed by the AI before they execute.</p>
            <div className="space-y-3" data-testid="actions-list">
              {actions.length === 0 && <p className="text-sm text-muted-foreground">No agent actions yet.</p>}
              {actions.map((a) => (
                <div key={a.id} className="border border-border rounded-sm p-4" data-testid={`action-${a.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-slate-950 text-white rounded-sm flex items-center justify-center">
                        {a.type === "create_tasks" ? <CheckSquare size={16} /> : <FileEdit size={16} />}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{a.title}</div>
                        <div className="label-mono text-slate-400">{a.description} · {a.status}</div>
                      </div>
                    </div>
                    {a.status === "draft" && (
                      <div className="flex gap-2">
                        <Button size="sm" className="rounded-sm bg-ai hover:bg-blue-800 h-8" onClick={() => approve(a.id)} data-testid={`approve-${a.id}`}>
                          <Check size={14} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => reject(a.id)} data-testid={`reject-${a.id}`}>
                          <X size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                  {a.payload?.tasks && (
                    <ul className="mt-3 ml-12 space-y-1">
                      {a.payload.tasks.map((t, i) => <li key={i} className="text-sm text-slate-700">• {typeof t === "string" ? t : t.title}</li>)}
                    </ul>
                  )}
                  {a.payload?.draft && <div className="mt-3 ml-12 text-sm text-slate-700 bg-muted rounded-sm p-3 whitespace-pre-wrap">{a.payload.draft}</div>}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
